import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth.js';
import { ApiError } from '../api.js';

export function SignupPage() {
  const { signupUser, state } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'authed') {
    navigate('/projects', { replace: true });
  }

  async function handle(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signupUser(email.trim(), password, displayName.trim() || undefined);
      navigate('/projects', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'EMAIL_TAKEN') {
          setError('That email is already registered. Try signing in instead.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Network error.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-sm mx-auto py-16">
      <h1 className="font-serif text-3xl mb-2">Create your account</h1>
      <p className="text-muted text-sm mb-8">
        Scriptvoice is in private alpha. Sign up below to claim your account.
      </p>
      <form onSubmit={handle} className="space-y-4">
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="email"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="name">Display name <span className="text-muted/60">(optional)</span></label>
          <input
            id="name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
            maxLength={120}
          />
        </div>
        <div>
          <label className="block text-sm text-muted mb-1.5" htmlFor="pw">Password</label>
          <input
            id="pw"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 font-mono text-sm"
            required
            minLength={8}
          />
          <p className="text-xs text-muted/70 mt-1">At least 8 characters.</p>
        </div>
        {error && (
          <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting || !email || password.length < 8}
          className="w-full bg-ink text-parchment py-2 rounded-md hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
        <p className="text-center text-sm text-muted">
          Already have an account?{' '}
          <Link to="/login" className="text-accent underline-offset-2 hover:underline">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
